/* Copyright (c) 2020-present CloudIO Inc */

import { IoPages, Query } from '@cloudio-saas/datasource-types';
import { useStateXAtom } from '@cloudio-saas/statex';
import { AppBar, Toolbar, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { memo } from 'react';
import useAppStore from 'store/useAppStore';
import { sidebarPageViewAtom } from 'layout/Sidebar/atoms';
import PageWizard from 'layout/Sidebar/panels/PagesView/Wizard/PageWizard';
import PageWizardNext from 'layout/Sidebar/panels/PagesView/Wizard/PageWizardNext';
import { WithSizeContext } from 'hooks/useComponentSize';
import { getIoPagesStoreProps } from 'store/utils/utils';
import PageControllerHistory from './PageControllerHistory';
import PageControllerHistoryHeader from './PageControllerHistoryHeader';
import PagesHistory from './PagesHistory';
import PagesAccess from './PagesAccess';
import PagesAccessHeader from './PagesAccessHeader';
import PagesEdit from './PagesEdit';
import PagesHeader from './PagesHeader';
import PagesList from './PagesList';
import PagesHistoryHeader from './PagesHistoryHeader';

export const pagesFilter: (appUid: string) => Query<IoPages> = (
  appUid: string,
) => ({
  filter: [{ appUid: { is: appUid } }],
  limit: 1000,
  projection: {
    pageName: 1,
    pageUid: 1,
    lastUpdateDate: 1,
    lastUpdatedBy: 1,
    appUid: 1,
    orgUid: 1,
    pageIcon: 1,
  },
  sort: { lastUpdateDate: -1 },
});

const useStyles = makeStyles((theme: Theme) => ({
  body: {
    overflowY: 'auto',
    overflowX: 'hidden',
    position: 'absolute',
    left: 0,
    right: 0,
    top: 65,
    bottom: 0,
  },
  header: {
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
  },
  title: {
    flex: 1,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  toolbar: {
    padding: 0,
  },
}));

function PagesPanel({
  appUid,
  pageUid,
  width,
}: {
  appUid: string;
  pageUid: string;
  width: number;
}): React.ReactElement {
  const { store, recordsOrder } = useAppStore<IoPages>(
    getIoPagesStoreProps(appUid),
  );
  store.read({ props: pagesFilter(appUid) });

  const classes = useStyles();

  const [view, setView] = useStateXAtom(sidebarPageViewAtom);

  let content;
  switch (view) {
    case 'list':
      content = (
        <PagesList
          recordsOrder={recordsOrder}
          setView={setView}
          store={store}
          pageUid={pageUid}
          appUid={appUid}
        />
      );
      break;
    case 'edit':
      content = (
        <PagesEdit appUid={appUid} pageUid={pageUid} setView={setView} />
      );
      break;
    case 'access':
      content = <PagesAccess appUid={appUid} />;
      break;
    case 'history':
      content = <PagesHistory appUid={appUid} />;
      break;
    case 'controllerHistory':
      content = <PageControllerHistory appUid={appUid} />;
      break;
    case 'wizard':
      content = (
        <PageWizard
          key="wizard"
          setView={setView}
          appUid={appUid}
          width={width}
        />
      );
      break;
    case 'next':
      content = <PageWizardNext key="next" setView={setView} appUid={appUid} />;
      break;
    default:
  }

  let toolBarContent;
  switch (view) {
    case 'edit':
    case 'list':
    case 'next':
    case 'wizard':
      toolBarContent = (
        <PagesHeader
          appUid={appUid}
          view={view}
          setView={setView}
          store={store}
        />
      );
      break;
    case 'access':
      toolBarContent = <PagesAccessHeader appUid={appUid} setView={setView} />;
      break;
    case 'history':
      toolBarContent = <PagesHistoryHeader appUid={appUid} setView={setView} />;
      break;
    case 'controllerHistory':
      toolBarContent = (
        <PageControllerHistoryHeader appUid={appUid} setView={setView} />
      );
      break;
    default:
  }

  return (
    <>
      <AppBar
        className={classes.header}
        position="relative"
        color="transparent"
      >
        <Toolbar className={classes.toolbar}>{toolBarContent}</Toolbar>
      </AppBar>
      <div className={classes.body}>
        <WithSizeContext>{content}</WithSizeContext>
      </div>
    </>
  );
}

export default memo(PagesPanel);
